"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
exports.__esModule = true;
var react_1 = require("react");
var ActionTypes_1 = require("./ActionTypes");
var useApolloNetworkStatusReducer_1 = require("./useApolloNetworkStatusReducer");
var useEventCallback_1 = require("./useEventCallback");
function isOperationType(operation, type) {
    return operation.query.definitions.some(function (definition) {
        return definition.kind === 'OperationDefinition' && definition.operation === type;
    });
}
function pendingOperations(type) {
    return function pendingOperationsByType(state, action) {
        if (state === void 0) { state = 0; }
        if (!isOperationType(action.payload.operation, type)) {
            return state;
        }
        switch (action.type) {
            case ActionTypes_1["default"].REQUEST:
                return state + 1;
            case ActionTypes_1["default"].ERROR:
            case ActionTypes_1["default"].SUCCESS:
            case ActionTypes_1["default"].CANCEL:
                // Just to be safe. See also the comment about `useEffect`
                // in `./useApolloNetworkStatusReducer.js`
                return Math.max(state - 1, 0);
        }
        return state;
    };
}
function latestOperationError(type) {
    return function latestOperationErrorByType(state, action) {
        if (!isOperationType(action.payload.operation, type)) {
            return state;
        }
        switch (action.type) {
            case ActionTypes_1["default"].REQUEST:
                return undefined;
            case ActionTypes_1["default"].ERROR: {
                var _a = action.payload, networkError = _a.networkError, operation = _a.operation;
                return { networkError: networkError, operation: operation };
            }
            case ActionTypes_1["default"].SUCCESS: {
                var _b = action.payload, result = _b.result, operation = _b.operation;
                if (result && result.errors) {
                    return { graphQLErrors: result.errors, response: result, operation: operation };
                }
                else {
                    return state;
                }
            }
        }
        return state;
    };
}
var pendingQueries = pendingOperations('query');
var pendingMutations = pendingOperations('mutation');
var queryError = latestOperationError('query');
var mutationError = latestOperationError('mutation');
function reducer(state, action) {
    if (isOperationType(action.payload.operation, 'subscription')) {
        return state;
    }
    var updatedState = __assign({}, state);
    // Pending operations
    updatedState.numPendingQueries = pendingQueries(updatedState.numPendingQueries, action);
    updatedState.numPendingMutations = pendingMutations(updatedState.numPendingMutations, action);
    // Latest errors
    updatedState.queryError = queryError(updatedState.queryError, action);
    updatedState.mutationError = mutationError(updatedState.mutationError, action);
    // The identity of the state should be kept if possible to avoid unnecessary re-renders.
    var haveValuesChanged = Object.keys(state).some(function (key) { return updatedState[key] !== state[key]; });
    return haveValuesChanged ? updatedState : state;
}
var initialState = {
    numPendingQueries: 0,
    numPendingMutations: 0,
    queryError: undefined,
    mutationError: undefined
};
function defaultShouldHandleOperation(operation) {
    // Enable opt-out per operation
    return operation.getContext().useApolloNetworkStatus !== false;
}
function useApolloNetworkStatus(dispatcher, options) {
    if (!options)
        options = {};
    var shouldHandleOperation = options.shouldHandleOperation || defaultShouldHandleOperation;
    // Performance optimization to allow changing this option
    // via props without causing the reducer to change.
    var shouldHandleOperationEventCallback = useEventCallback_1["default"](shouldHandleOperation);
    var configuredReducer = react_1.useMemo(function () { return function (state, action) {
        if (!shouldHandleOperationEventCallback(action.payload.operation)) {
            return state;
        }
        return reducer(state, action);
    }; }, [shouldHandleOperationEventCallback]);
    return useApolloNetworkStatusReducer_1["default"](dispatcher, configuredReducer, initialState);
}
exports["default"] = useApolloNetworkStatus;
